import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Can SAINT help if my antidepressants haven't worked?",
    answer:
      "SAINT® is FDA-cleared specifically for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. In a double-blind, sham-controlled trial, 78.6% of the active group reached remission at four weeks versus 13.3% of the sham group. The study was small (29 analyzed) and from a single site. Individual results vary.",
  },
  {
    question: "Do I need to have tried multiple antidepressants for SAINT?",
    answer:
      "The FDA-cleared indication requires that you have not achieved satisfactory improvement from prior antidepressant medication in the current episode. Your psychiatrist reviews your medication history to confirm eligibility. Many SAINT® patients have tried multiple antidepressants, but the specific number is a clinical decision.",
  },
  {
    question: "Can SAINT work if TMS already failed?",
    answer:
      "A prior course of standard TMS does not rule out SAINT®. SAINT® uses fMRI-guided targeting and an accelerated schedule that differs from conventional TMS. A meaningful share of SAINT® patients have already tried standard TMS. Bring your records for review.",
  },
];

export const pageCanSaintHelpAfterMeds: LearnPageData = {
  slug: "can-saint-help-if-antidepressants-didnt-work",
  title: "Can SAINT® Help If My Antidepressants Didn't Work? | Neuro Wellness",
  description:
    "SAINT® is FDA-cleared for adults whose depression hasn't improved on antidepressants. Learn about the evidence, eligibility, and how SAINT differs from medication.",
  canonical: "/learn/can-saint-help-if-antidepressants-didnt-work",
  h1: "Can SAINT® Help If Antidepressants Didn't Work?",
  breadcrumbLabel: "Can SAINT Help If Antidepressants Didn't Work?",
  crumbs: baseCrumbs("Can SAINT Help If Antidepressants Didn't Work?"),
  shortAnswer:
    "Yes. SAINT® is FDA-cleared specifically for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. In a double-blind, sham-controlled trial, 78.6% of the active group reached remission at four weeks versus 13.3% of the sham group. The study was small (29 analyzed) and from a single site. Individual results vary.",
  keyTakeaways: [
    "SAINT® is FDA-cleared for adults whose depression hasn't improved on antidepressants.",
    "In a sham-controlled trial, 78.6% of the active group reached remission at 4 weeks.",
    "SAINT® uses fMRI-guided targeting, not scalp measurements.",
    "Treatment is delivered over 5 consecutive days, not 6 weeks.",
    "Individual results vary and no outcome is guaranteed.",
  ],

  sections: [
    {
      heading: "How SAINT® Differs From Medication",
      body: (
        <>
          {P(
            "Antidepressants work through chemical changes in the brain's neurotransmitter systems. SAINT® uses magnetic pulses — intermittent theta burst stimulation — to stimulate a specific brain region chosen from your own functional MRI. It introduces no chemical into the body and does not require anesthesia. SAINT® is FDA-cleared, not FDA-approved (clearance is the regulatory pathway for devices; approval is for drugs).",
          )}
        </>
      ),
    },
    {
      heading: "The Evidence",
      body: (
        <>
          {P(
            "In the 2022 double-blind, sham-controlled randomized trial (Cole et al., Am J Psychiatry), 78.6% of the active treatment group (11 of 14) reached remission at four weeks compared with 13.3% (2 of 15) of the sham group. The 2020 open-label study reported 90.5% remission among completers, with an average time to remission of 2.6 days. Both studies were small and from a single site. These are trial statistics, not guarantees.",
          )}
        </>
      ),
    },
    {
      heading: "Do You Need Treatment-Resistant Depression for SAINT®?",
      body: (
        <>
          {P(
            "SAINT® is FDA-cleared for adults whose depression has not improved on antidepressant medication. This overlaps with common definitions of treatment-resistant depression, which typically requires inadequate response to at least two antidepressant trials of adequate dose and duration. The FDA-cleared indication does not specify a minimum number of failed medication trials — a psychiatrist determines whether your treatment history meets the criteria based on your individual circumstances.",
          )}
        </>
      ),
    },
    {
      heading: "Who May Be Eligible",
      body: (
        <>
          {P(
            "SAINT® is cleared for adults 22 and older with major depressive disorder who have not improved adequately on antidepressant medication. Eligibility also requires MRI safety screening (no ferromagnetic metal in the head, no implanted electronic devices) and screening for seizure history, bipolar disorder, and psychosis. A psychiatric evaluation determines eligibility.",
          )}
          {P(
            <>
              <a
                href="/am-i-a-candidate"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Check your eligibility for SAINT®
              </a>
              .
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation: "Cole EJ, et al. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation: "Cole EJ, et al. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/can-saint-help-if-antidepressants-didnt-work#webpage`,
        name: "Can SAINT Help If Antidepressants Didn't Work?",
        url: `${SITE_URL}/learn/can-saint-help-if-antidepressants-didnt-work`,
        description: "SAINT for adults whose antidepressants haven't worked.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/can-saint-help-if-antidepressants-didnt-work#faq`,
      ),
      breadcrumbJsonld(
        baseCrumbs("Can SAINT Help If Antidepressants Didn't Work?"),
      ),
    ],
  },
  relatedLinks: [
    {
      title: "When Depression Treatment Isn't Working",
      href: "/depression-treatment-not-working",
      description: "Full hub with all options",
    },
    {
      title: "SAINT® TMS",
      href: "/saint",
      description: "5-day accelerated TMS",
    },
    {
      title: "What Is SAINT® TMS?",
      href: "/learn/what-is-saint-tms",
      description: "How SAINT works",
    },
    {
      title: "Find a Verified SAINT® Provider",
      href: "/find-a-verified-saint-provider",
      description: "Verify provider status",
    },
  ],
};
